import React from "react";
import { Link } from "../i18n/router";
import { FileText } from "lucide-react";

/**
 * Shared page shell for /terms and /privacy. Preserves the original
 * placeholder chrome (round icon + centered heading + back link) and, when
 * `sections` are provided, renders a readable legal document inside an
 * eunoia-surface card. When no sections are passed, falls back to the
 * "Страница готовится" body so this component can still be reused as a
 * generic placeholder if needed.
 *
 * Section shape: { number, title, body: Array<string | { list: string[] }> }
 * - Plain string → paragraph
 * - Object with `list` → bulleted list
 */
export default function PlaceholderPage({ title, testId, lastUpdated, sections }) {
  const hasContent = Array.isArray(sections) && sections.length > 0;

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16">
      <div className={hasContent ? "max-w-3xl mx-auto" : "max-w-2xl mx-auto text-center"} data-testid={testId}>
        {/* Hero — icon + title (unchanged from original placeholder) */}
        <div className={hasContent ? "text-center mb-10" : ""}>
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
            style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}
          >
            <FileText className="h-10 w-10" style={{ color: "var(--eunoia-accent)" }} />
          </div>
          <h1 className="eunoia-title-large mb-4">{title}</h1>
          {hasContent ? (
            lastUpdated && (
              <p className="eunoia-caption" style={{ color: "var(--eunoia-soft)" }}>
                Последнее обновление: {lastUpdated}
              </p>
            )
          ) : (
            <p className="eunoia-body-large mb-8">Страница готовится</p>
          )}
        </div>

        {/* Body — legal sections when provided */}
        {hasContent && (
          <div className="eunoia-surface p-6 sm:p-10 space-y-8 text-left">
            {sections.map((section) => (
              <section key={section.number} data-testid={`legal-section-${section.number}`}>
                <h2 className="eunoia-title-small mb-3" style={{ color: "var(--eunoia-deep)" }}>
                  {section.number}. {section.title}
                </h2>
                <div className="space-y-3">
                  {section.body.map((block, idx) => {
                    if (typeof block === "string") {
                      return (
                        <p key={idx} className="eunoia-body">
                          {block}
                        </p>
                      );
                    }
                    if (block && Array.isArray(block.list)) {
                      return (
                        <ul key={idx} className="list-disc pl-5 space-y-2 eunoia-body">
                          {block.list.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Back link */}
        <div className={hasContent ? "text-center mt-10" : "mt-2"}>
          <Link
            to="/"
            className="eunoia-caption hover:underline"
            style={{ color: "var(--eunoia-accent)" }}
          >
            ← Вернуться на главную
          </Link>
        </div>
      </div>
    </div>
  );
}
